import { useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, Divider, List, ListItemButton, ListItemText, Typography, useMediaQuery, useTheme } from '@mui/material'
import { APPBAR_HEIGHT_REM, DESKTOP_BREAKPOINT, PAGE_TOC_WIDTH } from '~/common/constants/layout-constants'
import { useSidebarContext } from '~/common/context/sidebar/sidebar-context'
import { useFeatures } from '../use-features'

const toSectionId = (title) => title.toLowerCase().replace(/\s+/g, '-')

export const PageContents = ({ pageTitle, contents }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_BREAKPOINT))
  const { sidebarWidth } = useSidebarContext()
  const sectionRefs = useRef([])
  const location = useLocation()
  const { features } = useFeatures()

  const scrollToSection = (index) => {
    const el = sectionRefs.current[index]
    if (!el) return
    const remVal = parseFloat(APPBAR_HEIGHT_REM)
    const baseFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize)
    const appBarPx = remVal * baseFontSize
    const elementTop = el.getBoundingClientRect().top + window.scrollY
    window.scrollTo({ top: elementTop - appBarPx, behavior: 'smooth' })
  }

  useEffect(() => {
    const hash = location.hash
    if (!hash) return
    const id = hash.slice(1)
    const index = contents.findIndex(({ title }) => toSectionId(title) === id)
    if (index === -1) return
    setTimeout(() => scrollToSection(index), 100)
  }, [location.hash])

  return (
    <>
      {isDesktop && (
        <Box
          sx={{
            position: 'fixed',
            top: `calc(${APPBAR_HEIGHT_REM} + 56px)`,
            left: `calc(${sidebarWidth}px + 8px)`,
            zIndex: (theme) => theme.zIndex.appBar - 1,
            maxWidth: PAGE_TOC_WIDTH,
          }}
        >
          <List dense disablePadding>
            {contents.map(({ title }, i) => (
              <ListItemButton
                key={i}
                dense
                onClick={() => scrollToSection(i)}
                sx={{ borderRadius: 1, py: 0.25, px: 1 }}
              >
                <ListItemText
                  primary={title}
                  slotProps={{
                    primary: { variant: 'body2', color: 'text.secondary' }
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      )}
      <Box
        sx={{
          p: `${isDesktop ? '3px' : '14px'} 3px 3px ${isDesktop ? PAGE_TOC_WIDTH : '40px'}`,
          maxWidth: 900,
          mx: 'auto',
        }}
      >
        <Typography variant="h4" sx={{ mb: 3 }}>{pageTitle}</Typography>
        {contents.filter(({ requires }) => Boolean(!features[requires])).map(({ title, component }, i) => (
          <Box key={i} id={toSectionId(title)} ref={el => { sectionRefs.current[i] = el }}>
            {i > 0 && <Divider sx={{ my: 3 }} />}
            {component}
          </Box>
        ))}
      </Box>
    </>
  )
}
